"use client";

import { usePathname, useSearchParams } from "next/navigation";
import Link from "next/link";
import { ChevronLeft, ChevronRight } from "lucide-react";

type PaginationProps = {
  totalItems: number;
  itemsPerPage: number;
  currentPage: number;
};

export const Pagination = ({
  totalItems,
  itemsPerPage,
  currentPage,
}: PaginationProps) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  if (totalPages <= 1) return null;

  const createPageURL = (pageNumber: number) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", pageNumber.toString());
    return `${pathname}?${params.toString()}`;
  };

  return (
    <div className="flex items-center justify-center gap-2 mt-8">
      {currentPage > 1 ? (
        <Link
          href={createPageURL(currentPage - 1)}
          className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-green-main transition-colors"
          aria-label="Página anterior"
        >
          <ChevronLeft className="h-5 w-5" />
        </Link>
      ) : (
        <span className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-gray-300">
          <ChevronLeft className="h-5 w-5" />
        </span>
      )}

      {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
        // Mostrar primera, última, actual, anterior y siguiente
        if (
          page === 1 ||
          page === totalPages ||
          (page >= currentPage - 1 && page <= currentPage + 1)
        ) {
          return (
            <Link
              key={page}
              href={createPageURL(page)}
              className={`flex h-10 w-10 items-center justify-center rounded-md border ${
                currentPage === page
                  ? "bg-green-main border-green-main text-white"
                  : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:text-green-main"
              } transition-colors`}
            >
              {page}
            </Link>
          );
        }
        
        // Mostrar puntos suspensivos
        if (
          (page === 2 && currentPage > 3) || 
          (page === totalPages - 1 && currentPage < totalPages - 2)
        ) {
          return (
            <span
              key={page}
              className="flex h-10 w-10 items-center justify-center text-gray-500"
            >
              ...
            </span>
          );
        }

        return null;
      })}

      {currentPage < totalPages ? (
        <Link
          href={createPageURL(currentPage + 1)}
          className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-green-main transition-colors"
          aria-label="Página siguiente"
        >
          <ChevronRight className="h-5 w-5" />
        </Link>
      ) : (
        <span className="flex h-10 w-10 items-center justify-center rounded-md border border-gray-200 bg-gray-50 text-gray-300">
          <ChevronRight className="h-5 w-5" />
        </span>
      )}
    </div>
  );
};
